import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ContractFactory } from "ethers";
import { Panel } from "../components/common/Panel";
import ContractConsole from "../components/common/ContractConsole";
import { compileContract, getContract, getContracts, verifyContractPublic, registerContract, getFee, estimateContract } from "../lib/api";
import { shorten } from "../lib/format";
import { useWallet } from "../contexts/WalletContext";

const DEFAULT_SOL = `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.26;

contract SimpleStorage {
    uint256 public value;
    address public owner;
    event ValueChanged(uint256 value);

    constructor(uint256 _initial) {
        value = _initial;
        owner = msg.sender;
    }

    function set(uint256 v) public {
        value = v;
        emit ValueChanged(v);
    }

    function get() public view returns (uint256) {
        return value;
    }
}`;

export default function Contracts() {
  const { account, onBlokista, getSigner, connect, refreshBalance } = useWallet();
  const [tab, setTab] = useState("deploy");
  // deploy state
  const [source, setSource] = useState(DEFAULT_SOL);
  const [cname, setCname] = useState("SimpleStorage");
  const [abi, setAbi] = useState("");
  const [bytecode, setBytecode] = useState("");
  const [cargs, setCargs] = useState("100");
  const [compiling, setCompiling] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [msg, setMsg] = useState(null);
  const [cerr, setCerr] = useState("");
  const [dEst, setDEst] = useState(null);
  const [dEstBusy, setDEstBusy] = useState(false);
  // interact state
  const [iaddr, setIaddr] = useState("");
  const [iabi, setIabi] = useState(null);
  const [ierr, setIerr] = useState("");
  const [recent, setRecent] = useState([]);
  // verify state
  const [vaddr, setVaddr] = useState("");
  const [vsource, setVsource] = useState(DEFAULT_SOL);
  const [vcname, setVcname] = useState("SimpleStorage");
  const [vcompiler, setVcompiler] = useState("0.8.26");
  const [vbusy, setVbusy] = useState(false);
  const [vmsg, setVmsg] = useState(null);
  const [verr, setVerr] = useState("");

  useEffect(() => { getContracts().then((d) => setRecent(d.contracts)).catch(() => {}); }, [msg]);

  const compile = async () => {
    setCompiling(true); setCerr(""); setAbi(""); setBytecode("");
    try {
      const r = await compileContract(source, cname);
      setAbi(JSON.stringify(r.abi, null, 2));
      setBytecode(r.bytecode);
    } catch (e) { setCerr(e?.response?.data?.detail || "Compile failed"); }
    finally { setCompiling(false); }
  };

  const deploy = async () => {
    setDeploying(true); setMsg(null); setCerr("");
    try {
      const parsedAbi = JSON.parse(abi);
      const argList = cargs.trim() ? cargs.split(",").map((s) => s.trim()) : [];
      const signer = await getSigner();
      const factory = new ContractFactory(parsedAbi, bytecode, signer);
      let overrides = {};
      try { const fee = await getFee(); overrides = { gasPrice: BigInt(fee.gasPriceWei) }; } catch { /* estimate */ }
      const contract = await factory.deploy(...argList, overrides);
      await contract.waitForDeployment();
      const address = await contract.getAddress();
      const deployTx = contract.deploymentTransaction()?.hash;
      await registerContract({ address, abi: parsedAbi, name: cname, deployTx, deployer: account }).catch(() => {});
      refreshBalance(account);
      setMsg({ address, txHash: deployTx });
    } catch (e) { setCerr(e?.shortMessage || e?.message || "Deploy failed. Compile first."); }
    finally { setDeploying(false); }
  };

  const estimateDeploy = async () => {
    setDEstBusy(true); setDEst(null);
    try {
      const parsedAbi = JSON.parse(abi);
      const constructorArgs = cargs.trim() ? cargs.split(",").map((s) => s.trim()) : [];
      const r = await estimateContract({ abi: parsedAbi, bytecode, constructorArgs });
      setDEst(r);
    } catch (e) {
      setDEst({ error: e?.response?.data?.detail || "Cannot estimate" });
    } finally { setDEstBusy(false); }
  };

  const loadAbi = async () => {
    setIerr(""); setIabi(null);
    try {
      const c = await getContract(iaddr);
      if (!c.isContract) { setIerr("No contract code at this address"); return; }
      if (!c.abi) { setIerr("ABI not known. Deploy via this console or verify its source to register the ABI."); return; }
      setIabi(c.abi);
    } catch (e) { setIerr(e?.response?.data?.detail || "Failed to load"); }
  };

  const submitVerify = async () => {
    setVbusy(true); setVerr(""); setVmsg(null);
    try {
      const r = await verifyContractPublic({ address: vaddr.trim(), source: vsource, contractName: vcname, compilerVersion: vcompiler });
      setVmsg(r);
    } catch (e) { setVerr(e?.response?.data?.detail || "Verification failed"); }
    finally { setVbusy(false); }
  };
  const validVaddr = /^0x[a-fA-F0-9]{40}$/.test(vaddr.trim());

  return (
    <div className="fade-in" data-testid="contracts-page">
      <div className="mb-4">
        <span className="eyebrow" style={{ color: "var(--amber)" }}>Developer</span>
        <h1 className="font-display text-[24px] font-extrabold mt-1">Contract Studio</h1>
        <p className="text-[13px] mt-1" style={{ color: "var(--muted)" }}>Compile &amp; deploy Solidity with your wallet, then read/write any contract. Every transaction pays the network fee in {" "}<span style={{ color: "var(--amber)" }}>BCC</span>.</p>
      </div>

      <div className="flex gap-2 mb-4">
        <button className={`nav-link ${tab === "deploy" ? "active" : ""}`} onClick={() => setTab("deploy")} data-testid="tab-deploy">Deploy</button>
        <button className={`nav-link ${tab === "interact" ? "active" : ""}`} onClick={() => setTab("interact")} data-testid="tab-interact">ABI Console</button>
        <button className={`nav-link ${tab === "verify" ? "active" : ""}`} onClick={() => setTab("verify")} data-testid="tab-verify">Verify Source</button>
      </div>

      {tab === "deploy" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Panel title="Solidity Source">
            <div className="p-4">
              <div className="flex gap-2 mb-3">
                <input className="input-dark text-[12px]" value={cname} onChange={(e) => setCname(e.target.value)} placeholder="Contract name" data-testid="contract-name-input" />
                <button className="btn-amber px-4 py-2 text-[12px] whitespace-nowrap" onClick={compile} disabled={compiling} data-testid="compile-btn">{compiling ? "Compiling…" : "Compile"}</button>
              </div>
              <textarea className="ta-dark" rows={16} value={source} onChange={(e) => setSource(e.target.value)} data-testid="solidity-source" />
              {cerr && <div className="mt-2 text-[12px] p-2 rounded mono break-all" style={{ background: "rgba(242,85,90,0.1)", color: "var(--red)" }} data-testid="compile-error">{cerr}</div>}
            </div>
          </Panel>

          <Panel title="Compiled Artifact &amp; Deploy">
            <div className="p-4">
              <div className="eyebrow mb-1">ABI</div>
              <textarea className="ta-dark mb-3" rows={7} value={abi} onChange={(e) => setAbi(e.target.value)} placeholder="Compile to generate ABI…" data-testid="abi-output" />
              <div className="eyebrow mb-1">Bytecode</div>
              <input className="input-dark mb-3 text-[12px]" value={bytecode ? shorten(bytecode, 20, 8) : ""} readOnly placeholder="0x…" data-testid="bytecode-output" />
              <div className="eyebrow mb-1">Constructor Args (comma separated)</div>
              <input className="input-dark mb-3 text-[12px]" value={cargs} onChange={(e) => setCargs(e.target.value)} placeholder="e.g. 100" data-testid="constructor-args" />
              {abi && bytecode && (
                <button className="nav-link w-full mb-2" onClick={estimateDeploy} disabled={dEstBusy} data-testid="estimate-deploy-btn">
                  {dEstBusy ? "Estimating…" : <><i className="fa-solid fa-gauge-high mr-1.5" />Estimate Deploy Cost</>}
                </button>
              )}
              {dEst && (
                dEst.error
                  ? <div className="mb-2 text-[11.5px]" style={{ color: "var(--muted-2)" }} data-testid="deploy-est">{dEst.error}</div>
                  : <div className="mb-2 text-[12px] mono p-2 rounded" style={{ background: "rgba(88,166,255,0.08)", color: "var(--blue)" }} data-testid="deploy-est">Est. deploy cost: ~{dEst.feeBcc.toFixed(8)} {dEst.symbol} ({dEst.gas.toLocaleString()} gas × {dEst.gasPriceGwei} gwei)</div>
              )}
              {!account ? (
                <button className="btn-amber w-full" onClick={connect} data-testid="deploy-connect-btn"><i className="fa-solid fa-wallet mr-2" />Connect Wallet to Deploy</button>
              ) : !onBlokista ? (
                <div className="text-[12px] text-center py-2" style={{ color: "var(--red)" }}>Switch your wallet to Blokista Chain to deploy.</div>
              ) : (
                <button className="btn-amber w-full" onClick={deploy} disabled={deploying || !abi || !bytecode} data-testid="deploy-btn">
                  {deploying ? "Deploying…" : "Deploy Contract"}
                </button>
              )}
              {msg && (
                <div className="mt-3 p-3 rounded-lg" style={{ background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.25)" }} data-testid="deploy-success">
                  <div className="font-display font-semibold" style={{ color: "var(--green)" }}><i className="fa-solid fa-circle-check mr-1" /> Deployed!</div>
                  <div className="mt-1 text-[12px]" style={{ color: "var(--muted)" }}>Address: <Link to={`/address/${msg.address}`} className="link-amber mono">{msg.address}</Link></div>
                  <button className="link-amber text-[12px] mt-1" onClick={() => { setIaddr(msg.address); setTab("interact"); }} data-testid="interact-deployed">Open in ABI Console →</button>
                </div>
              )}
            </div>
          </Panel>

          <Panel title="Recently Deployed" className="lg:col-span-2" bodyClass="overflow-auto">
            <table className="dt">
              <thead><tr><th>Name</th><th>Address</th><th>Deployer</th><th></th></tr></thead>
              <tbody>
                {recent.map((c) => (
                  <tr key={c.address} data-testid={`recent-contract-${c.address.slice(0,8)}`}>
                    <td className="font-display font-semibold">{c.name}{c.isToken && <span className="pill pill-amber ml-2">BCC-20</span>}</td>
                    <td><Link to={`/address/${c.address}`} className="link-amber mono text-[12px]">{shorten(c.address, 8, 6)}</Link></td>
                    <td className="mono text-[12px]" style={{ color: "var(--muted)" }}>{shorten(c.deployer, 6, 4)}</td>
                    <td><button className="link-amber text-[12px]" onClick={() => { setIaddr(c.address); setTab("interact"); }}>Interact →</button></td>
                  </tr>
                ))}
                {recent.length === 0 && <tr><td colSpan={4} className="text-center py-6" style={{ color: "var(--muted)" }}>None yet</td></tr>}
              </tbody>
            </table>
          </Panel>
        </div>
      ) : tab === "interact" ? (
        <div>
          <Panel title="Load Contract" className="mb-5">
            <div className="p-4 flex flex-col md:flex-row gap-2">
              <input className="input-dark text-[12px]" value={iaddr} onChange={(e) => setIaddr(e.target.value)} placeholder="Contract address 0x…" data-testid="interact-address-input" />
              <button className="btn-amber px-5 whitespace-nowrap" onClick={loadAbi} data-testid="load-abi-btn">Load ABI</button>
            </div>
            {ierr && <div className="px-4 pb-4 text-[12px]" style={{ color: "var(--red)" }} data-testid="interact-error">{ierr}</div>}
          </Panel>
          {iabi && <ContractConsole address={iaddr} abi={iabi} />}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Panel title="Source Code">
            <div className="p-4">
              <p className="text-[12.5px] mb-3" style={{ color: "var(--muted)" }}>
                Publish your Solidity source (Etherscan-style). Anyone can verify — the source is accepted only if its compiled bytecode matches the on-chain contract.
              </p>
              <div className="flex gap-2 mb-3">
                <input className="input-dark text-[12px]" value={vcname} onChange={(e) => setVcname(e.target.value)} placeholder="Contract name" data-testid="verify-cname-input" />
                <input className="input-dark text-[12px]" value={vcompiler} onChange={(e) => setVcompiler(e.target.value)} placeholder="Compiler (0.8.26)" data-testid="verify-compiler-input" />
              </div>
              <textarea className="ta-dark" rows={16} value={vsource} onChange={(e) => setVsource(e.target.value)} data-testid="verify-source" />
            </div>
          </Panel>

          <Panel title="Contract to Verify">
            <div className="p-4">
              <div className="eyebrow mb-1">Deployed Contract Address</div>
              <input className="input-dark mb-3 text-[12px]" value={vaddr} onChange={(e) => setVaddr(e.target.value)} placeholder="0x…" data-testid="verify-address-input" />
              <button className="btn-amber w-full" onClick={submitVerify} disabled={vbusy || !validVaddr} data-testid="verify-submit-btn">
                {vbusy ? <><i className="fa-solid fa-circle-notch fa-spin mr-2" /> Verifying…</> : <><i className="fa-solid fa-circle-check mr-2" /> Verify &amp; Publish</>}
              </button>
              {verr && <div className="mt-3 text-[12px] p-2 rounded mono break-all" style={{ background: "rgba(242,85,90,0.1)", color: "var(--red)" }} data-testid="verify-error">{verr}</div>}
              {vmsg && (
                <div className="mt-3 p-3 rounded-lg" style={{ background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.25)" }} data-testid="verify-success">
                  <div className="font-display font-semibold" style={{ color: "var(--green)" }}><i className="fa-solid fa-circle-check mr-1" /> Source verified &amp; published!</div>
                  <div className="mt-1 text-[12px]" style={{ color: "var(--muted)" }}>
                    Address: <Link to={`/address/${vmsg.address}`} className="link-amber mono">{shorten(vmsg.address, 8, 6)}</Link> now shows a verified source badge for everyone.
                  </div>
                </div>
              )}
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}
